
import { Header } from "@/components/layout/Header";
import { cn } from "@/lib/utils";
import { SetupGuideCTA } from "@/components/setup-guide/SetupGuideCTA";
import { useTheme } from "@/components/ThemeProvider";
import { useLocation } from "react-router-dom";
import { BugReportButton } from "@/components/admin/bug-reports/BugReportButton";

interface ShellProps {
  children: React.ReactNode;
  className?: string;
}

export function Shell({ children, className }: ShellProps) {
  const { theme } = useTheme();
  const location = useLocation();
  
  const is24SevenTheme = theme === "24seven";
  const isContractsPage = location.pathname === "/contracts";
  
  return (
    <div 
      className={cn(
        "min-h-screen flex flex-col transition-colors duration-300 overflow-x-hidden", 
        {
          "bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800": !is24SevenTheme,
          "bg-24seven-light": is24SevenTheme
        }
      )} 
      data-app-shell
    >
      <Header />
      <main
        className={cn(
          "flex-1 container mx-auto py-6 sm:py-10 md:py-14 px-4 sm:px-8 md:px-10 animate-fade-in relative",
          { 
            "max-w-screen-2xl": is24SevenTheme && !isContractsPage,
            "max-w-full": isContractsPage
          },
          className
        )}
        data-main-content
      >
        <div className="relative w-full">
          {/* Decorative elements - not visible in 24seven theme */}
          {!is24SevenTheme && !isContractsPage && (
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
            </div>
          )}
          
          {/* Content */}
          <div className={cn(
            "relative z-10 w-full",
            {
              "grid gap-6": is24SevenTheme && !isContractsPage
            }
          )}>
            {children}
          </div>
        </div>
      </main>
      <BugReportButton />
      <SetupGuideCTA variant="floating" />
    </div>
  );
}
